import React, { useCallback, useEffect, useState } from 'react'
import { Box, IconButton } from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import {
  IconSystemChevronLeft,
  IconSystemChevronRight,
} from '@moonpig/launchpad-assets'
import { system as s } from '@moonpig/launchpad-system'
import useEmblaCarousel from 'embla-carousel-react'
import { useLocaleText } from './locale'
import { CarouselDots } from './ImageCarouselDots'

const BUTTON_WIDTH = 48

const FixedSizeIconButton = styled(IconButton)`
  ${s({ display: { xs: 'none', md: 'flex' }, width: BUTTON_WIDTH })};
`

type ImageCarouselProps = {
  items: React.JSX.Element[]
  title: string
  imageDimensions?: {
    width: number
    height: number
  }
  initialItemIndex?: number
  carouselRef?: React.RefObject<HTMLDivElement>
}

export const ImageCarousel = ({
  items,
  title,
  imageDimensions = { width: 680, height: 680 },
  initialItemIndex = 0,
  carouselRef,
}: ImageCarouselProps) => {
  const t = useLocaleText()

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    startIndex: initialItemIndex,
  })

  const onDotClick = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index)
    },
    [emblaApi],
  )

  const onPrevButtonClick = useCallback(() => {
    emblaApi?.scrollPrev()
  }, [emblaApi])

  const onNextButtonClick = useCallback(() => {
    emblaApi?.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback(() => {
    setPrevBtnDisabled(!emblaApi?.canScrollPrev())
    setNextBtnDisabled(!emblaApi?.canScrollNext())
    setSelectedIndex(emblaApi?.selectedScrollSnap() || 0)
    setScrollSnaps(
      emblaApi?.scrollSnapList()?.length
        ? emblaApi.scrollSnapList()
        : items.map((_, index) => index),
    )
  }, [emblaApi, items])

  useEffect(() => {
    onSelect()
    emblaApi?.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  return (
    <Box
      maxWidth={imageDimensions && imageDimensions.width + BUTTON_WIDTH * 2}
      margin="0 auto"
      data-testid="lp-find-image-carousel"
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={3}
      title={title}
    >
      <Box display="flex" alignItems="center" width="100%">
        <FixedSizeIconButton
          icon={IconSystemChevronLeft}
          label={t('image_carousel.previous_image')}
          onClick={onPrevButtonClick}
          disabled={prevBtnDisabled}
        />
        <Box
          ref={emblaRef}
          overflow="hidden"
          width="100%"
          className="carousel__slider"
        >
          <Box display="flex" ref={carouselRef}>
            {items.map((carouselItem, index) => {
              const key = carouselItem.key || /* istanbul ignore next */ index
              return (
                <Box key={key} flex="0 0 100%">
                  {carouselItem}
                </Box>
              )
            })}
          </Box>
        </Box>
        <FixedSizeIconButton
          icon={IconSystemChevronRight}
          label={t('image_carousel.next_image')}
          onClick={onNextButtonClick}
          disabled={nextBtnDisabled}
        />
      </Box>
      <CarouselDots
        scrollSnaps={scrollSnaps}
        selectedIndex={selectedIndex}
        onDotClick={onDotClick}
      />
    </Box>
  )
}
